import {
  CreateParams,
  CreateResult,
  fetchUtils,
  HttpError,
  Identifier,
  RaRecord,
} from 'react-admin';
import { defaultHeaders, isJsonApiMultiResourceLink, isJsonApiResourceLink } from './utils';
import {
  createReactAdminValidationError,
  getJsonApiErrorBody,
  getJsonApiErrorMessage,
  getJsonApiErrorStatus,
  JsonApiResource,
  JsonApiSingleResponse,
} from './JsonApiTypes';

export default function create(apiUrl: string, httpClient = fetchUtils.fetchJson) {
  return async <T extends RaRecord<Identifier>>(
    resource: string,
    params: CreateParams<T>,
  ): Promise<CreateResult<T & JsonApiResource>> => {
    const { attributes = {}, relationships = {} } = params.data as Partial<JsonApiResource>;

    const validRelationships = Object.entries(relationships).reduce(
      (acc, [key, value]) => {
        if (isJsonApiResourceLink(value) || isJsonApiMultiResourceLink(value)) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, unknown>,
    );

    const payload = {
      data: {
        type: resource,
        attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
        relationships: Object.keys(validRelationships).length > 0 ? validRelationships : undefined,
      },
    } as Partial<JsonApiResource>;

    const query = new URLSearchParams();

    params.meta?.prefetch?.forEach((prefetch: string) => {
      query.append('include', prefetch);
    });

    const url = `${apiUrl}/${resource}?${query}`;

    try {
      const response = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: defaultHeaders,
      });
      const result = response.json as JsonApiSingleResponse;
      const { data } = result;

      if (!data) {
        throw new HttpError(
          'The server returned an empty response',
          response.status,
          response.body,
        );
      }

      const prefetched = result.included?.reduce(
        (acc, includedRecord) => {
          acc[includedRecord.type] ??= [];
          acc[includedRecord.type].push(includedRecord);
          return acc;
        },
        {} as Record<string, JsonApiResource[]>,
      );

      return {
        data,
        meta: { prefetched },
      } as CreateResult<T & JsonApiResource>;
    } catch (error) {
      const validationErrors = createReactAdminValidationError(error as HttpError);
      throw new HttpError(
        getJsonApiErrorMessage(error as HttpError, 'Error creating record'),
        getJsonApiErrorStatus(error as HttpError),
        {
          ...validationErrors,
          apiResponse: getJsonApiErrorBody(error),
        },
      );
    }
  };
}
